import { Injectable } from "@angular/core";
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpErrorResponse,
  HttpEvent,
} from "@angular/common/http";
import { AuthServiceUser } from "./auth.service";
import { catchError, throwError, Observable, switchMap } from "rxjs";
import { Router } from "@angular/router";
import { environment } from "src/environments/environment";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private environmentInfo = environment;
  constructor(private authService: AuthServiceUser, private router: Router) {
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Attach the token to the request headers
    const token = this.authService.getToken();
    let modifiedReq = req;

    // Only modify the request if a token exists
    if (token) {
      modifiedReq = req.clone({
        setHeaders: {
          Authorization: `${token}`,
          'ngrok-skip-browser-warning':  '69420'
        },
      });
    }

    // Handle the HTTP request
    return next
      .handle(modifiedReq)
      .pipe(catchError((error) => this.errorHandler(error, req, next)));
  }

  private errorHandler = (
    error: HttpErrorResponse,
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> => {
    let errorMessage = "An unknown error occurred";

    // Handle different HTTP errors
    if (error.status === 404) {
      errorMessage = "Resource not found";
    } else if (error.status === 401 && !req.url.includes(this.environmentInfo.authRefreshToken)) {
      // Token expired, attempt to refresh
      return this.authService.refreshTokenRequest().pipe(
        switchMap((newAccessToken: string) => {
          // Retry the original request with the new token
          const retryReq = req.clone({
            setHeaders: {
              Authorization: `${newAccessToken}`,
            },
          });
          return next.handle(retryReq);
        }),
        catchError((refreshError) => {
          // Refresh token failed, log out the user
          this.authService.logout();
          this.router.navigate(["/login"]);
          return throwError(() => refreshError);
        })
      );
    } else if (error.status === 401) {
      errorMessage = "Unauthorized access. Redirecting to login.";
      this.authService.logout();
      this.router.navigate(["/login"]);
    } else if (error.status === 500) {
      errorMessage = error.error && error.error.message ? error.error.message : "Server error occurred";
    } else if (error.status === 400) {
      errorMessage = error.error && error.error.message ? error.error.message : "Bad request";
    } else if (error.status === 422) {
      errorMessage = error.error && error.error.message ? error.error.message :"Unprocessable Entity";
    } else {
      errorMessage = `HTTP Error: ${error.message}`;
    }

    console.error(errorMessage, error);
    return throwError(() => ({
      message: errorMessage,
      status: error.status,
      details: error.error,
    }));
  };
}
